import React, { useMemo, useState, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Container, Form, Row, Col, Table } from "react-bootstrap";
import rawData from "../../assets/data/summary_table.json";
import { DATENSTAND } from "../../config/constants";
import { exportToExcel } from "../../utils/exportExcel";
import { THERAPY_COLORS } from "../../config/colors";

// --- number formatters (match Zeitverlauf tables) ---
const nfPct1 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const fmtPct = (v) => (v == null ? "–" : nfPct1.format(v));

const nfInt = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 0,
});
const fmtInt = (v) => (v == null ? "–" : nfInt.format(v));

const BAR_SIZE = 80;

const NO_INFO_LABEL = "keine gemeldete Primärtherapie";

const assignColors = (groups) => {
  const map = {};
  const count = {};

  groups.forEach((group) => {
    if (group === NO_INFO_LABEL) {
      map[group] = THERAPY_COLORS.NO_INFO[0];
      return;
    }

    const prefix =
      Object.keys(THERAPY_COLORS)
        .filter((p) => p !== "NO_INFO" && p !== "FALLBACK")
        .find((p) => group.startsWith(p)) || "FALLBACK";

    const index = count[prefix] || 0;

    map[group] =
      THERAPY_COLORS[prefix][index % THERAPY_COLORS[prefix].length];

    count[prefix] = index + 1;
  });

  return map;
};

// --- staging systems per entity ---
const STAGING_SYSTEMS = [
  {
    system: "Ann Arbor",
    matchIncludes: ["lymphom", "non-hodgkin", "hodgkin"],
    allowed: ["I", "II", "III", "IV", "X"],
    labelFor: (stage) => `Ann Arbor ${stage}`,
  },
  {
    system: "AJCC - Amercian Joint Committee on Cancer",
    matchIncludes: ["malignes melanom der haut"],
    allowed: ["I", "II", "III", "IV", "X"],
    labelFor: (stage) => `AJCC ${stage}`,
  },
  {
    system: "ISS - International Staging System",
    matchIncludes: ["myelom", "plasma", "multiples myelom"],
    allowed: ["I", "II", "III", "X"],
    labelFor: (stage) => `ISS ${stage}`,
  },
  {
    system: "Binet",
    matchIncludes: ["cll", "chronische lymphatische leukämie", "leukämie"],
    allowed: ["I", "II", "III", "X"],
    labelFor: (stage) => {
      const map = { I: "A", II: "B", III: "C", X: "X" };
      const letter = map[stage];
      return letter ? `Binet ${letter}` : `Binet ${stage}`;
    },
  },
];

// helpers for unique lists
const getStages = (data) => [...new Set(data.map((d) => d.uicc))].sort();
const getYears = (data) => [...new Set(data.map((d) => d.diayear))].sort();
const getCancers = (data) => [...new Set(data.map((d) => d.entit))].sort();

const getGroups = (data) => {
  const unique = [...new Set(data.map((d) => d.primther))];

  return unique.sort((a, b) => {
    if (a === NO_INFO_LABEL) return 1;
    if (b === NO_INFO_LABEL) return -1;
    return a.localeCompare(b);
  });
};

// prepare chart data (with optional stage labeling)
const prepareChartData = (entries, axisType, allGroups, labelForStage) => {
  const grouped = {};

  entries.forEach(({ diayear, uicc, primther, percent }) => {
    const key = axisType === "year" ? diayear : uicc;
    const axisKey = axisType === "year" ? "year" : "uiccF";
    const axisValue =
      axisType === "year"
        ? key
        : labelForStage
        ? labelForStage(String(key))
        : key;

    if (!grouped[key]) {
      grouped[key] = { [axisKey]: axisValue };
      allGroups.forEach((g) => {
        grouped[key][g] = 0;
      });
    }

    grouped[key][primther] = percent;
  });

  const axisKey = axisType === "year" ? "year" : "uiccF";

  return Object.values(grouped).sort((a, b) => {
    const va = a[axisKey];
    const vb = b[axisKey];

    if (typeof va === "number" && typeof vb === "number") {
      return va - vb;
    }

    return String(va).localeCompare(String(vb));
  });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const filtered = payload.filter((p) => p.value > 0);

  const sorted = [
    ...filtered
      .filter((p) => p.name !== NO_INFO_LABEL)
      .sort((a, b) => b.value - a.value),
    ...filtered.filter((p) => p.name === NO_INFO_LABEL),
  ];

  return (
    <div className="bg-white p-2 border rounded shadow-sm">
      <strong>{label}</strong>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {sorted.map((entry, i) => (
          <li
            key={i}
            style={{
              color: entry.name === NO_INFO_LABEL ? "#000000" : entry.color,
            }}
          >
            {entry.name}: {nfPct1.format(entry.value)}%
          </li>
        ))}
      </ul>
    </div>
  );
};

const Alltherapie = () => {
  const [viewMode, setViewMode] = useState("byStage");
  const [selectedStage, setSelectedStage] = useState("I");
  const [selectedYear, setSelectedYear] = useState(() => {
  const yearsFromData = getYears(rawData).map(Number);
  return Math.max(...yearsFromData);
});
  const [selectedCancer, setSelectedCancer] = useState("Bauchspeicheldrüse");

  const filteredRaw = useMemo(() => {
    return rawData.filter((d) => {
      if (selectedCancer === "Brustdrüse" && d.SEX === "M") return false;
      return d.entit === selectedCancer;
    });
  }, [selectedCancer]);

  const cancers = useMemo(() => getCancers(rawData), []);

  const rawStages = useMemo(() => getStages(filteredRaw), [filteredRaw]);
  const years = useMemo(() => getYears(filteredRaw), [filteredRaw]);

  const stagingConfig = useMemo(() => {
    const lowerName = (selectedCancer || "").toLowerCase();

    return (
      STAGING_SYSTEMS.find((sys) =>
        sys.matchIncludes.some((fragment) =>
          lowerName.includes(fragment.toLowerCase())
        )
      ) || null
    );
  }, [selectedCancer]);

  const tableStageHeader = useMemo(() => {
    if (viewMode === "byYear") return "Jahr";

    if (!stagingConfig) return "Stadium (UICC - Stadium)";

    return `Stadium (${stagingConfig.system})`;
  }, [viewMode, stagingConfig]);

  const stageLabel = useCallback(
    (stage) =>
      stagingConfig && stage != null
        ? stagingConfig.labelFor(String(stage))
        : stage,
    [stagingConfig]
  );

  const stages = useMemo(() => {
    if (!stagingConfig) {
      return rawStages;
    }

    const allowedSet = new Set(stagingConfig.allowed);
    return rawStages.filter((s) => allowedSet.has(s));
  }, [rawStages, stagingConfig]);

  const allGroups = useMemo(() => getGroups(filteredRaw), [filteredRaw]);
  const colorMap = useMemo(() => assignColors(allGroups), [allGroups]);

  const filterData = (sexCode) => {
    const filtered = filteredRaw.filter((d) => {
      if (d.SEX !== sexCode) return false;

      return viewMode === "byYear"
        ? d.uicc === selectedStage
        : d.diayear === selectedYear;
    });

    return prepareChartData(
      filtered,
      viewMode === "byYear" ? "year" : "stage",
      allGroups,
      viewMode === "byYear" ? null : stageLabel
    );
  };

  const generateTableData = () => {
    const rows = [];
    const categories = viewMode === "byYear" ? years : stages;
    const key = viewMode === "byYear" ? "diayear" : "uicc";

    const fixedFilter =
      viewMode === "byYear"
        ? { key: "uicc", value: selectedStage }
        : { key: "diayear", value: selectedYear };

    categories.forEach((cat) => {
      const maleRows = filteredRaw.filter(
        (d) =>
          d.SEX === "M" &&
          d[key] === cat &&
          d[fixedFilter.key] === fixedFilter.value
      );

      const femaleRows = filteredRaw.filter(
        (d) =>
          d.SEX === "W" &&
          d[key] === cat &&
          d[fixedFilter.key] === fixedFilter.value
      );

      const allCombos = new Set([
        ...maleRows.map((d) => d.primther),
        ...femaleRows.map((d) => d.primther),
      ]);

      rows.push({ header: cat });

      [...allCombos]
        .sort((a, b) => {
          if (a === NO_INFO_LABEL) return 1;
          if (b === NO_INFO_LABEL) return -1;

          const aVal = Math.max(
            maleRows.find((d) => d.primther === a)?.percent || 0,
            femaleRows.find((d) => d.primther === a)?.percent || 0
          );

          const bVal = Math.max(
            maleRows.find((d) => d.primther === b)?.percent || 0,
            femaleRows.find((d) => d.primther === b)?.percent || 0
          );

          return bVal - aVal;
        })
        .forEach((combo) => {
          const maleRow = maleRows.find((d) => d.primther === combo);
          const femaleRow = femaleRows.find((d) => d.primther === combo);

          rows.push({
            combo,
            maleN: maleRow?.n ?? null,
            femaleN: femaleRow?.n ?? null,
            malePct: maleRow?.percent ?? null,
            femalePct: femaleRow?.percent ?? null,
          });
        });
    });

    return rows;
  };

  const tableData = useMemo(
    () => generateTableData(),
    [
      viewMode,
      selectedStage,
      selectedYear,
      selectedCancer,
      filteredRaw,
      stages,
      years,
    ]
  );

  const handleExport = () => {
    const levelLabel = viewMode === "byYear" ? "Jahr" : "Stadium";
    const excelRows = [];
    let currentHeader = null;

    tableData.forEach((row) => {
      if (row.header != null) {
        currentHeader = row.header;
      } else {
        excelRows.push({
          [levelLabel]:
            viewMode === "byYear"
              ? currentHeader
              : stageLabel(currentHeader),
          Behandlung: row.combo,
          "Männer (n)": row.maleN ?? "",
          "Frauen (n)": row.femaleN ?? "",
        });
      }
    });

    exportToExcel({
      filename: "therapien",
      sheetName: "Therapien",
      data: excelRows,
      metadata: [
        ["Therapien"],
        ["Krebsregister Sachsen-Anhalt https://www.kr-lsa.de"],
        [`Datenstand: ${DATENSTAND}`],
        [`Export am: ${new Date().toLocaleString("de-DE")}`],
      ],
    });
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">Therapien</h2>
      <div className="section-title-underline"></div>

      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Im Laufe der letzten Jahrzehnte haben sich viele Therapiemöglichkeiten
          für Krebserkrankungen verändert und erweitert. Auf dieser Seite sind
          die verschiedenen Therapiekombinationen für Frauen und Männer separat
          dargestellt. Sie haben die Möglichkeit, sich für die 15 häufigsten
          Entitäten die dokumentierten Therapien für verschiedene Diagnosejahre
          und verschiedene Stadien anzeigen zu lassen.
        </p>
        <p>
          Bitte beachten Sie, dass nur gemeldete Therapien im Krebsregister
          dokumentiert werden können. Die Option „Keine Information“ kommt immer
          dann vor, wenn keine Therapie stattfand bzw. keine Meldung an das
          Krebsregister erfolgte.
        </p>
      </div>

      <button onClick={handleExport} className="top-btn mb-3">
        📥 Daten als Excel exportieren
      </button>

      <Form.Check
        type="switch"
        id="viewToggle"
        label={`Ansicht: ${viewMode === "byYear" ? "Jahre" : "Stadium"}`}
        checked={viewMode === "byYear"}
        onChange={() =>
          setViewMode((prev) => (prev === "byYear" ? "byStage" : "byYear"))
        }
        className="mb-3"
      />

      <div
        className="d-flex align-items-center gap-3 mb-4"
        style={{ maxWidth: 700 }}
      >
        <Form.Group controlId="entitSelect" className="flex-fill">
          <Form.Label>Krebs-Auswahl:</Form.Label>
          <Form.Select
            value={selectedCancer}
            onChange={(e) => setSelectedCancer(e.target.value)}
          >
            {cancers.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="yearStageSelect" className="flex-fill">
          <Form.Label>
            {viewMode === "byYear" ? "Stadium:" : "Jahr:"}
          </Form.Label>
          <Form.Select
            value={viewMode === "byYear" ? selectedStage : selectedYear}
            onChange={(e) =>
              viewMode === "byYear"
                ? setSelectedStage(e.target.value)
                : setSelectedYear(+e.target.value)
            }
          >
            {(viewMode === "byYear" ? stages : years).map((v) => (
              <option key={v} value={v}>
                {viewMode === "byYear" ? stageLabel(v) : v}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      <Row className="mb-4">
        {[
          { code: "M", label: "Männer" },
          { code: "W", label: "Frauen" },
        ].map(({ code, label }) => {
          const data = filterData(code);

          return (
            <Col md={6} key={code}>
              <h5>{label}</h5>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={data} barCategoryGap="40%">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={viewMode === "byYear" ? "year" : "uiccF"}
                  />
                  <YAxis
                    domain={[0, 100]}
                    unit="%"
                    ticks={[0, 20, 40, 60, 80, 100]}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} />

                  {allGroups.map((group) => (
                    <Bar
                      key={group}
                      dataKey={group}
                      stackId={code}
                      fill={colorMap[group]}
                      name={group}
                      barSize={BAR_SIZE}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Col>
          );
        })}
      </Row>

      <Table
        responsive
        className="zeitverlauf-table text-center align-middle"
        style={{ maxWidth: "1000px" }}
      >
        <thead>
          <tr>
            <th className="text-start">{tableStageHeader}</th>
            <th colSpan={2}>Anzahl</th>
          </tr>
          <tr>
            <th className="text-start">Behandlung</th>
            <th>Männer</th>
            <th>Frauen</th>
          </tr>
        </thead>

        <tbody>
          {tableData.map((row, idx) =>
            row.header ? (
              <tr key={`h-${idx}`} className="table-secondary">
                <td colSpan={3} className="fw-bold text-start">
                  {viewMode === "byYear"
                    ? row.header
                    : stageLabel(row.header)}
                </td>
              </tr>
            ) : (
              <tr key={`r-${idx}`}>
                <td className="text-start">
                  <span
                    style={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      backgroundColor: colorMap[row.combo],
                      marginRight: 8,
                      verticalAlign: "middle",
                    }}
                  />
                  <span style={{ verticalAlign: "middle" }}>{row.combo}</span>
                </td>
                <td>{fmtInt(row.maleN)}</td>
                <td>{fmtInt(row.femaleN)}</td>
              </tr>
            )
          )}
        </tbody>
      </Table>
    </Container>
  );
};

export default Alltherapie;
